import {
    Box,
    Typography,
    Button,
    TextField,
    Grid,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { CARDIO_TYPEN, INTENSITAETEN, btnSx } from './cardioConstants';

// Formular-Dialog zum Anlegen/Bearbeiten einer Cardio-Session.
function CardioFormDialog({ open, onClose, editSession, form, onForm, onSaveConfirm, formValid, loading, hatDistanz, isMobile }) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                {editSession ? 'Session bearbeiten' : 'Neue Cardio-Session'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>

                    {/* Datum + Dauer */}
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Datum *"
                                type="date"
                                fullWidth
                                value={form.datum}
                                onChange={e => onForm({ ...form, datum: e.target.value })}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Dauer (Minuten) *"
                                type="number"
                                fullWidth
                                value={form.dauer_minuten}
                                onChange={e => onForm({ ...form, dauer_minuten: e.target.value })}
                                slotProps={{ htmlInput: { min: 1, max: 600 } }}
                                placeholder="z.B. 45"
                            />
                        </Grid>
                    </Grid>

                    {/* Cardio-Typ */}
                    <Box>
                        <Typography variant="body2" color="text.secondary" mb={1} sx={{ fontWeight: 500 }}>
                            Cardio-Typ *
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {CARDIO_TYPEN.map(typ => (
                                <Chip
                                    key={typ.value}
                                    label={`${typ.emoji} ${typ.label}`}
                                    onClick={() => onForm({ ...form, cardio_typ: typ.value, distanz_km: '' })}
                                    variant={form.cardio_typ === typ.value ? 'filled' : 'outlined'}
                                    color={form.cardio_typ === typ.value ? 'primary' : 'default'}
                                    sx={{ cursor: 'pointer', fontWeight: form.cardio_typ === typ.value ? 700 : 400 }}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Intensität */}
                    <Box>
                        <Typography variant="body2" color="text.secondary" mb={1} sx={{ fontWeight: 500 }}>
                            Intensität
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {INTENSITAETEN.map(i => (
                                <Chip
                                    key={i.value}
                                    label={i.label}
                                    onClick={() => onForm({ ...form, intensitaet: form.intensitaet === i.value ? '' : i.value })}
                                    variant={form.intensitaet === i.value ? 'filled' : 'outlined'}
                                    sx={{
                                        cursor: 'pointer',
                                        borderColor: i.color,
                                        color: form.intensitaet === i.value ? '#fff' : i.color,
                                        bgcolor: form.intensitaet === i.value ? i.color : 'transparent',
                                        fontWeight: form.intensitaet === i.value ? 700 : 400,
                                        '&:hover': { bgcolor: `${i.color}22` },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Divider />

                    {/* Messwerte */}
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', mt: -1 }}>
                        Messwerte (optional)
                    </Typography>
                    <Grid container spacing={2}>
                        {hatDistanz && (
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    label="Distanz (km)"
                                    type="number"
                                    fullWidth
                                    value={form.distanz_km}
                                    onChange={e => onForm({ ...form, distanz_km: e.target.value })}
                                    slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
                                    placeholder="z.B. 5.30"
                                />
                            </Grid>
                        )}
                        <Grid size={{ xs: 6 }}>
                            <TextField
                                label="Ø Herzfrequenz (bpm)"
                                type="number"
                                fullWidth
                                value={form.durchschnitts_bpm}
                                onChange={e => onForm({ ...form, durchschnitts_bpm: e.target.value })}
                                slotProps={{ htmlInput: { min: 40, max: 250 } }}
                                placeholder="z.B. 145"
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField
                                label="Max. HF (bpm)"
                                type="number"
                                fullWidth
                                value={form.max_bpm}
                                onChange={e => onForm({ ...form, max_bpm: e.target.value })}
                                slotProps={{ htmlInput: { min: 40, max: 250 } }}
                                placeholder="z.B. 178"
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField
                                label="Kalorien (kcal)"
                                type="number"
                                fullWidth
                                value={form.kalorien}
                                onChange={e => onForm({ ...form, kalorien: e.target.value })}
                                slotProps={{ htmlInput: { min: 0 } }}
                                placeholder="z.B. 320"
                            />
                        </Grid>
                    </Grid>

                    {/* Notizen */}
                    <TextField
                        label="Notizen"
                        multiline
                        rows={3}
                        fullWidth
                        value={form.notizen}
                        onChange={e => onForm({ ...form, notizen: e.target.value })}
                        placeholder="Wie hat sich die Session angefühlt?"
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', borderRadius: '12px' }}>
                    Abbrechen
                </Button>
                <Button
                    onClick={onSaveConfirm}
                    variant="contained"
                    disabled={!formValid || loading}
                    sx={{ ...btnSx, padding: '10px 24px' }}
                >
                    {editSession ? 'Aktualisieren' : 'Speichern'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default CardioFormDialog;
